import { useId, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Plus } from "lucide-react";
import { useRates, useSaveRate, type ExchangeRate } from "@/api/books";
import { CURRENCY_CODE } from "@/api/fx";
import { RateForm } from "@/components/forms/RateForm";
import { RequestError } from "@/components/RequestError";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { EmptyState } from "@/components/ui/empty-state";
import { TextField } from "@/components/ui/field";
import { formatDate } from "@/lib/format";

type RateDefaults = { currency: string; on: string };
type Editing = { mode: "new"; defaults?: RateDefaults } | { mode: "edit"; rate: ExchangeRate } | null;

/** `?tab=rates&currency=USD&on=2026-10-07` asks for that rate: the form opens ready for it. */
function askedFor(params: URLSearchParams): RateDefaults | null {
  const currency = (params.get("currency") ?? "").toUpperCase();
  const on = params.get("on") ?? "";
  return CURRENCY_CODE.test(currency) && /^\d{4}-\d{2}-\d{2}$/.test(on) ? { currency, on } : null;
}

export function RatesPanel() {
  const id = useId();
  const [params, setParams] = useSearchParams();
  const [currency, setCurrency] = useState("");
  const [editing, setEditingState] = useState<Editing>(() => {
    const defaults = askedFor(params);
    return defaults ? { mode: "new", defaults } : null;
  });
  const rates = useRates(currency.trim().toUpperCase() || undefined);
  const save = useSaveRate();

  function setEditing(next: Editing): void {
    // Done with the rate a link asked for: drop it, so a reload does not ask again.
    if (next === null && askedFor(params)) setParams({ tab: "rates" }, { replace: true });
    setEditingState(next);
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <TextField
          id={`${id}-currency`}
          label="Currency"
          value={currency}
          onChange={(v) => setCurrency(v.toUpperCase())}
          placeholder="All"
          maxLength={3}
        />
        {editing === null && (
          <Button size="sm" onClick={() => setEditing({ mode: "new" })}>
            <Plus className="h-4 w-4" aria-hidden />
            Add rate
          </Button>
        )}
      </div>
      <p className="text-sm text-muted-foreground">
        A foreign amount is valued at the latest typed rate on or before its date. An entry already
        posted keeps the rate it used; repost it from the Journals tab to apply a corrected rate.
      </p>
      {editing && (
        <Card className="border-primary/40">
          <CardHeader>
            <CardTitle>{editing.mode === "new" ? "New rate" : "Edit rate"}</CardTitle>
          </CardHeader>
          <CardContent>
            <RateForm
              initial={editing.mode === "edit" ? editing.rate : null}
              defaults={editing.mode === "new" ? editing.defaults : undefined}
              onSubmit={async (input) => {
                await save.mutateAsync({
                  input,
                  existing: editing.mode === "edit" ? editing.rate : undefined,
                });
                setEditing(null);
              }}
              onCancel={() => setEditing(null)}
              onReload={() => {
                void rates.refetch();
                setEditing(null);
              }}
            />
          </CardContent>
        </Card>
      )}
      <RequestError error={rates.error} />
      {rates.data && rates.data.length === 0 ? (
        <EmptyState
          title="No rates yet"
          description="Type the rupee rate for each currency you invoice or receive in, on the days it matters."
        />
      ) : (
        <div className="table-frame">
          <table className="data-table">
            <thead className="border-b bg-muted/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-4 py-2 font-medium">Date</th>
                <th className="px-4 py-2 font-medium">Currency</th>
                <th className="px-4 py-2 text-right font-medium">Rupees per unit</th>
                <th className="px-4 py-2 font-medium">Source</th>
                <th className="px-4 py-2 font-medium">Typed by</th>
                <th className="px-4 py-2">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {(rates.data ?? []).map((rate) => (
                <tr key={rate.id} className="border-b last:border-0">
                  <td className="whitespace-nowrap px-4 py-2">{formatDate(rate.on)}</td>
                  <td className="px-4 py-2">{rate.currency}</td>
                  <td className="whitespace-nowrap px-4 py-2 text-right tabular-nums">{rate.rate_inr}</td>
                  <td className="px-4 py-2">{rate.source}</td>
                  <td className="px-4 py-2 text-muted-foreground">{rate.typed_by_username ?? "—"}</td>
                  <td className="px-4 py-2 text-right">
                    <Button size="sm" variant="ghost" onClick={() => setEditing({ mode: "edit", rate })}>
                      Edit
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
